/**
 * Single source of truth for audio/sound content (white noise + meditation).
 * Used by AudioPlayer and ContentLibrary.
 */

export type SoundContentType = 'white_noise' | 'meditation';

export interface SoundTheme {
  title: string; // i18n key for name
  poeticSubtitle: string; // i18n key
  description: string; // i18n key for library description
  backgroundImage: string;
  duration: number;
  gradientFrom: string;
  gradientTo: string;
  audioSrc: string | null;
}

export interface SoundThemeRecord {
  [id: string]: SoundTheme & { type: SoundContentType };
}

export const soundThemes: SoundThemeRecord = {
  rain: {
    title: 'library.content.rain.name',
    poeticSubtitle: 'library.content.rain.poetic',
    description: 'library.content.rain.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=1200&q=80',
    duration: 3600,
    gradientFrom: '#52658f',
    gradientTo: '#7388b3',
    audioSrc: '/sounds/rain.mp3',
    type: 'white_noise'
  },
  forest: {
    title: 'library.content.forest.name',
    poeticSubtitle: 'library.content.forest.poetic',
    description: 'library.content.forest.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=1200&q=80',
    duration: 3000,
    gradientFrom: '#1a4d2e',
    gradientTo: '#2a784b',
    audioSrc: '/audios/forest.mp3',
    type: 'white_noise'
  },
  wind: {
    title: 'library.content.wind.name',
    poeticSubtitle: 'library.content.wind.poetic',
    description: 'library.content.wind.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80',
    duration: 3300,
    gradientFrom: '#708090',
    gradientTo: '#a9b4bf',
    audioSrc: '/audios/wind.mp3',
    type: 'white_noise'
  },
  fireplace: {
    title: 'library.content.fireplace.name',
    poeticSubtitle: 'library.content.fireplace.poetic',
    description: 'library.content.fireplace.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1579761804843-f997ade7fa35?q=80&w=3540',
    duration: 2400,
    gradientFrom: '#d15e26',
    gradientTo: '#f39c12',
    audioSrc: '/audios/fireplace.mp3',
    type: 'white_noise'
  },
  breathing: {
    title: 'library.content.breathing.name',
    poeticSubtitle: 'library.content.breathing.poetic',
    description: 'library.content.breathing.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80',
    duration: 900,
    gradientFrom: '#8B5CF6',
    gradientTo: '#A78BFA',
    audioSrc: '/audios/breathing.mp3',
    type: 'meditation'
  },
  'body-scan': {
    title: 'library.content.bodyScan.name',
    poeticSubtitle: 'library.content.bodyScan.poetic',
    description: 'library.content.bodyScan.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=1200&q=80',
    duration: 1200,
    gradientFrom: '#059669',
    gradientTo: '#10B981',
    audioSrc: '/audios/body-scan.mp3',
    type: 'meditation'
  },
  gratitude: {
    title: 'library.content.gratitude.name',
    poeticSubtitle: 'library.content.gratitude.poetic',
    description: 'library.content.gratitude.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80',
    duration: 720,
    gradientFrom: '#DC2626',
    gradientTo: '#F87171',
    audioSrc: '/audios/gratitude.mp3',
    type: 'meditation'
  },
  'let-it-go': {
    title: 'library.content.letItGo.name',
    poeticSubtitle: 'library.content.letItGo.poetic',
    description: 'library.content.letItGo.desc',
    backgroundImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80',
    duration: 1080,
    gradientFrom: '#7C3AED',
    gradientTo: '#A855F7',
    audioSrc: '/audios/let-it-go.mp3',
    type: 'meditation'
  },
  default: {
    title: 'library.content.default.name',
    poeticSubtitle: 'library.content.default.poetic',
    description: 'library.content.default.poetic',
    backgroundImage: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1200&q=80',
    duration: 3600,
    gradientFrom: '#2c3e50',
    gradientTo: '#34495e',
    audioSrc: null,
    type: 'white_noise'
  }
};

export const WHITE_NOISE_IDS = ['rain', 'forest', 'wind', 'fireplace'] as const;
export const MEDITATION_IDS = ['breathing', 'body-scan', 'gratitude', 'let-it-go'] as const;

export type SoundThemeId = keyof typeof soundThemes;
